import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search,
  User,
  MapPin,
  Layout,
  Book,
  FileText,
  ChevronRight,
  Zap,
  Image,
  BookOpen,
  BarChart3,
  Settings,
  ClipboardList,
  Kanban,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Person } from '../../types.ts';
import { useTranslation } from '../../i18n.ts';
import { Modal } from '../ui/Modal.tsx';
import { formatFullName, personMatchesQuery } from '../../utils/nameUtils.ts';
import { geboorteOfDoopLabel } from '../../utils/personVitals.ts';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  people: Person[];
}

interface ResultItem {
  id: string;
  label: string;
  icon: React.FC<{ size: number }>;
  path: string;
  sub?: string;
  /** Een persoon-treffer; zie `TREE_GOTO_PERSON_EVENT`. */
  personId?: string;
}

/**
 * NC #697 — Emma zocht vanuit de stamboom via het vergrootglas en klikte een
 * persoon aan: "dan wordt die de proband en is mijn hele stamboom view foetsie".
 * Sta je al ín de boom, dan wil je naar de persoon toe gebracht worden, niet dat
 * de boom om hem heen herbouwd wordt. De boom luistert naar dit event en doet
 * hetzelfde als bij `/tree?personId=X` vanuit een ander scherm (NC #841): boom
 * zoals ingesteld, persoon selecteren en erheen vliegen; buiten bereik eerst de
 * generaties ophogen, pas als laatste redmiddel de focus verleggen.
 */
export const TREE_GOTO_PERSON_EVENT = 'pt:tree-goto-person';

// Memoized result item — prevents re-render of ALL items on selection change
const PaletteItem = React.memo<{
  item: ResultItem;
  index: number;
  isSelected: boolean;
  onSelect: (item: ResultItem) => void;
}>(({ item, index, isSelected, onSelect }) => (
  <button
    data-idx={index}
    onClick={() => onSelect(item)}
    className={`w-full flex items-center justify-between p-4 rounded-2xl transition-colors transition-shadow text-left ${
      isSelected
        ? 'bg-pt-forest text-pt-gold shadow-lg'
        : 'hover:bg-pt-cream dark:hover:bg-white/5 text-pt-forest dark:text-[var(--text-main)]'
    }`}
  >
    <div className="flex items-center gap-4">
      <div
        className={`p-2 rounded-xl ${isSelected ? 'bg-pt-gold text-pt-forest' : 'bg-pt-cream dark:bg-white/5 text-pt-gold-text dark:text-[var(--gold-text)]'}`}
      >
        <item.icon size={18} />
      </div>
      <div>
        <p className="font-bold">{item.label}</p>
        {item.sub && (
          <p
            className={`text-[10px] uppercase font-black tracking-widest ${
              // #1519 — op de groene geselecteerde rij was sage-text (~2:1) onleesbaar.
              isSelected ? 'text-pt-sage' : 'text-pt-sage-text dark:text-[var(--text-muted)]'
            }`}
          >
            {item.sub}
          </p>
        )}
      </div>
    </div>
    <ChevronRight
      size={16}
      className={
        isSelected
          ? 'opacity-100 translate-x-1 transition-transform transition-opacity'
          : 'opacity-20'
      }
    />
  </button>
));

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, people }) => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  // Pre-build labels once when people changes (avoids re-computing per keystroke).
  // Het matchen zelf gaat via personMatchesQuery zodat prefix/maiden/diakritieken
  // consistent worden afgehandeld (#760).
  const searchablePeople = useMemo(
    () =>
      people.map((p) => {
        // #1519 (NC #768) — geboorte, anders 'ged. <doopdatum>'; gedeeld sinds #1523.
        return {
          person: p,
          label: formatFullName(p.givenNames, p.surnamePrefix, p.surname, p.patronym),
          sub: geboorteOfDoopLabel(p, t, lang),
        };
      }),
    [people, lang, t]
  );

  const results = useMemo(() => {
    const q = query.toLowerCase();
    const modules: ResultItem[] = [
      { id: 'm-dash', label: t('dashboard'), icon: Layout, path: '/' },
      { id: 'm-tree', label: t('tree'), icon: User, path: '/tree' },
      { id: 'm-people', label: t('people'), icon: User, path: '/people' },
      { id: 'm-src', label: t('sources'), icon: Book, path: '/sources' },
      { id: 'm-plc', label: t('places'), icon: MapPin, path: '/places' },
      { id: 'm-media', label: t('media'), icon: Image, path: '/media' },
      { id: 'm-stories', label: t('stories'), icon: BookOpen, path: '/stories' },
      { id: 'm-planner', label: t('planner'), icon: Kanban, path: '/planner' },
      { id: 'm-quality', label: t('dataQuality'), icon: ClipboardList, path: '/analyse' },
      { id: 'm-stats', label: t('statistics'), icon: BarChart3, path: '/statistics' },
      { id: 'm-rep', label: t('reports'), icon: FileText, path: '/reports' },
      { id: 'm-import', label: t('importExport'), icon: FileText, path: '/import' },
      { id: 'm-account', label: t('account'), icon: Settings, path: '/account' },
    ].filter((m) => m.label.toLowerCase().includes(q));

    const personResults: ResultItem[] = searchablePeople
      .filter((sp) => personMatchesQuery(sp.person, query))
      .slice(0, 20)
      .map((sp) => ({
        id: `p-${sp.person.id}`,
        label: sp.label,
        icon: User,
        path: `/tree?personId=${sp.person.id}`,
        sub: sp.sub,
        personId: sp.person.id,
      }));

    return [...modules, ...personResults];
  }, [query, searchablePeople, t]);

  // Stable callback for item selection
  const handleSelect = useCallback(
    (item: ResultItem) => {
      if (!item) return;
      onClose();
      if (item.personId && location.pathname === '/tree') {
        const personId = item.personId;
        setTimeout(
          () =>
            window.dispatchEvent(new CustomEvent(TREE_GOTO_PERSON_EVENT, { detail: { personId } })),
          50
        );
        return;
      }
      // Use setTimeout to ensure the palette closes before navigation
      // and force re-render even when navigating to the same route with different params
      setTimeout(() => navigate(item.path, { state: { ts: Date.now() } }), 50);
    },
    [navigate, onClose, location.pathname]
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
      return;
    }
    const handleKeys = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') setSelectedIndex((s) => Math.min(results.length - 1, s + 1));
      if (e.key === 'ArrowUp') setSelectedIndex((s) => Math.max(0, s - 1));
      if (e.key === 'Enter') handleSelect(results[selectedIndexRef.current]);
    };
    window.addEventListener('keydown', handleKeys);
    return () => window.removeEventListener('keydown', handleKeys);
  }, [isOpen, results, handleSelect]);

  // Event delegation for mouse hover — onMouseOver fires only at element boundaries (not every pixel)
  const handleMouseOver = useCallback((e: React.MouseEvent) => {
    const btn = (e.target as HTMLElement).closest('[data-idx]');
    if (btn) {
      const idx = Number(btn.getAttribute('data-idx'));
      if (idx !== selectedIndexRef.current) setSelectedIndex(idx);
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleId="modal-title-command"
      size="md"
      className="!rounded-[32px]"
    >
      <div className="p-4 md:p-6 border-b border-pt-forest/5 dark:border-white/10 flex items-center gap-4">
        <Search size={24} className="text-pt-gold-text dark:text-[var(--gold-text)]" />
        <input
          id="command-palette-search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('quickSearch') + '...'}
          aria-label={t('quickSearch')}
          className="flex-1 bg-transparent border-none outline-none text-lg font-bold text-pt-forest dark:text-[var(--text-main)] placeholder:text-pt-sage-text/40 dark:placeholder:text-[var(--text-muted)]"
        />
        <div className="px-2 py-1 bg-pt-cream dark:bg-white/5 rounded text-[9px] font-black uppercase text-pt-sage-text dark:text-[var(--text-muted)] border border-pt-forest/5 dark:border-white/10">
          ESC
        </div>
      </div>

      <div
        className="max-h-[50vh] overflow-y-auto p-4 space-y-1 no-scrollbar"
        onMouseOver={handleMouseOver}
      >
        <h2 id="modal-title-command" className="sr-only">
          {t('quickSearch')}
        </h2>
        {results.map((res, idx) => (
          <PaletteItem
            key={res.id}
            item={res}
            index={idx}
            isSelected={idx === selectedIndex}
            onSelect={handleSelect}
          />
        ))}
        {results.length === 0 && (
          <div className="py-20 text-center space-y-4">
            <Zap className="mx-auto text-pt-cream dark:text-white/10" size={48} />
            <p className="text-pt-sage-text dark:text-[var(--text-muted)] italic font-medium">
              {t('noResultsFound')} &quot;{query}&quot;
            </p>
          </div>
        )}
      </div>

      <div className="p-4 bg-pt-cream/50 dark:bg-[var(--bg-surface)] border-t border-pt-forest/5 dark:border-white/10 flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-pt-sage-text dark:text-[var(--text-muted)]">
        <div className="flex gap-4">
          <span className="flex items-center gap-1">
            <ChevronRight size={10} /> {t('selectAction')}
          </span>
          <span className="flex items-center gap-1">
            <ChevronRight size={10} className="rotate-90" /> {t('navigate')}
          </span>
        </div>
        <span>Pecktree v2.0</span>
      </div>
    </Modal>
  );
};

export default React.memo(CommandPalette);
